import {
  useRef,
  Children,
  cloneElement,
  ReactElement,
  useEffect,
  FC,
} from "react";
import cn from "classnames";
import { createBemBlockBuilder } from "@ebsco-ui/utils";
import { useShowMoreContext } from "../show-more-context";

export interface ShowMoreItemsProps {
  children: ReactElement[];
}

const ShowMoreItems: FC<ShowMoreItemsProps> = ({ children }) => {
  const showMoreItemRef = useRef<HTMLElement>(null);
  const { isExpanded, limit, setTotalItems } = useShowMoreContext();
  const allItems = Children.toArray(children) as ReactElement[];
  const itemsToRender = allItems.slice(0, isExpanded ? allItems.length : limit);
  const numberOfChildren = Children.count(children);

  useEffect(() => {
    setTotalItems(numberOfChildren);
  }, [numberOfChildren, setTotalItems]);

  useEffect(() => {
    if (showMoreItemRef.current) {
      showMoreItemRef.current.focus();
    }

    if (isExpanded && !showMoreItemRef.current) {
      console.error(
        "The ShowMore component provides a ref and tabindex to the components passed as items. Please make sure that these components can accept refs and tabindex as props.",
      );
    }
  }, [isExpanded, showMoreItemRef]);

  const getClassNames = (classNames: string[]) => {
    const getBlocksWith = createBemBlockBuilder(classNames);
    return cn(
      getBlocksWith(),
      getBlocksWith(isExpanded ? "--expanded" : "--collapsed"),
    );
  };

  const visibleItems = itemsToRender.map((child, index) => {
    const baseProps = {
      ...child.props,
      className: getClassNames([child.props.className, "eb-show-more-item"]),
      key: index,
    };

    let focusableProps;
    if (index === limit) {
      focusableProps = {
        ref: showMoreItemRef,
        tabIndex: -1,
      };
    }

    return cloneElement(child, { ...baseProps, ...focusableProps });
  });

  return <>{visibleItems}</>;
};

export default ShowMoreItems;
